import React, { useCallback, useState, useEffect } from "react";
import { Grid, Theme } from "@mui/material";
import classNames from "classnames";
import { INonFlyingAssignment } from "@vmosolutions/oc-interfaces";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";
import { v1 } from "uuid";
import { useDrag } from "react-dnd";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

export type INonFlightChip = Partial<INonFlyingAssignment>;

interface INonFlightChipProps {
  data?: INonFlightChip;
  className?: string;
  onClickChip?: (flightId: string) => void;
}

export const NonFlightChip = (props: INonFlightChipProps): JSX.Element => {
  const { data, className, onClickChip } = props;
  const [, setContextAnchor] = useState(null);
  const isICAO = useSelector((state: IRootState) => state.authentication.appSettings.blockInfo.icao);
  //!! Watch out because only a limited number of airports are stored in the redux store.
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const [locCode, setLocCode] = useState(data.locationICAO);
  const dragType = "NONFLIGHT";
  const [collected, drag, dragPreview] = useDrag(
    {
      type: dragType,
      item: { flight: props?.data, type: dragType },
    },
    [dragType, data]
  );

  useEffect(() => {
    if (!data?.locationICAO) {
      setLocCode(null);
      return;
    }
    if (isICAO) {
      setLocCode(data.locationICAO);
    } else setLocCode(airportOptions[data.locationICAO]?.iata ?? data.locationICAO);
  }, [airportOptions, data.locationICAO, isICAO]);

  // Set context menu anchor (opens it);
  const handlePopoverOpen = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event.preventDefault();
    event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
    setContextAnchor(event.currentTarget);
  }, []);

  if (data) {
    const { _id, locationICAO, assignmentType } = data;
    const title = !!assignmentType && assignmentType.title ? assignmentType.title : "Non-Flying Event";
    const location = locationICAO ? locationICAO : "";
    const color = assignmentType && assignmentType.defaultColor ? assignmentType.defaultColor : "#000000";

    const nonFlightId = _id ? _id : v1();
    // Event has properly formatted ID
    const assignedEvent: boolean = _id && !_id.includes("temp");

    if (!title || !location || !color) {
      return null;
    }

    // If all required data is passed, render the flight chip.
    const chipClass = nonFlightId + "-nonflightChip";
    const frColor = color ? (assignedEvent ? invertHexColor(color) : `#${color}`) : "white";
    const bgColor = color ? (assignedEvent ? `#${color}` : "transparent") : "black";
    const borderStyle = color ? (assignedEvent ? "none" : `1px solid #${color}`) : "1px solid white";

    const PairingStyles = (theme: Theme): any => ({
      ContextMenuChip: {
        userSelect: "none",
        width: 120,
        height: 26,
        boxShadow: "2px 2px 5px " + theme.palette.common.black,
        justifyContent: "space-between",
        display: "flex",
        flexDirection: "row",
        margin: "2px",
        borderRadius: "2px",
        fontFamily: "B612 Mono, arial, sans-serif", // Cousine showed poor anti-aliasing (blurred)
        fontSize: 10,
        fontWeight: "normal",
        padding: "5px",
        alignItems: "center",
        color: theme.palette.primary.contrastText, // This get's overriden by the inline style applied to assign best contrast color.
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shortest,
        }),
        "&.cancelled": {
          opacity: 0.75,
          userSelect: "none",
          // pointerEvents: "none", //! this is needed for tooltips to work.
          "& .cancelled_icon": {
            marginLeft: "-5px",
          },
        },
        "& .info": {
          // position: "absolute",
          width: 110,
          "& .flightnumber, & .event_title": {
            fontSize: 12,
            fontWeight: "bold",
            padding: "0px 2px",
          },
          "& .origin, & .destination ": {
            padding: "0px 2px",
          },
        },
        "&.draggable": {
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        },
        "&.posflightchip div": { padding: 0 },
        "&.nonflightchip div": {
          padding: 0,
          "&.title": { fontSize: 12 },
        },
        "& div": {
          display: "flex",
          flexDirection: "row",
          padding: "0px 5px",
        },
        "& .details": {
          flexDirection: "column",
          "& div": { lineHeight: "9px", padding: 0 },
        },
        "& .flightnumber, & .event_title, & .airline_flight": {
          fontSize: 12,
          fontWeight: "bold",
        },
        "&.interactive": {
          "&:hover": { margin: "2px 0px", width: 124 },
          "&:active": { margin: "2px 4px", width: 116 },
        },
      },
    });

    return (
      <Grid
        ref={drag}
        item
        id={nonFlightId}
        key={nonFlightId}
        style={{
          color: frColor,
          backgroundColor: bgColor,
          border: borderStyle,
        }}
        sx={(theme) => PairingStyles(theme).ContextMenuChip}
        className={classNames(className, "nonflightchip", !!onClickChip && !!_id && "interactive", chipClass)}
        onClick={() => !!onClickChip && !!_id && onClickChip(_id)}
        onContextMenu={handlePopoverOpen}
      >
        <Grid item className="title">
          {title}
        </Grid>
        <Grid item className="location">
          {locCode}
        </Grid>
      </Grid>
    );
  }
  return null;
};
